import Divider from "antd/lib/divider";
import { type ReactNode, useMemo, useState } from "react";

import { getResourceIRI, OrderingDirection, UserRole, useUserListQuery, useUserQuery } from "@sol/sdk";
import { Select, type SelectProps } from "@sol/uikit/data-entry/Select";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { Pagination } from "@sol/uikit/navigation/Pagination/Pagination";

const RESULTS_PER_PAGE = 20;

const readSelectedTrainer = (value: SelectProps["value"]) => (typeof value === "string" ? value : undefined);

export const TrainerSelect = ({ onSelect, onChange, value, ...rest }: SelectProps) => {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const selectedTrainerIri = readSelectedTrainer(value);

    const { data, isLoading } = useUserListQuery({
        filters: {
            role: UserRole.TRAINER,
            groups: ["user:read:minimal"],
            emails: search || undefined,
        },
        pagination: { page: currentPage, perPage: RESULTS_PER_PAGE },
        order: { by: "email", direction: OrderingDirection.ASC },
    });

    // Keeps the selected trainer labelled even when the results moved past their page
    const { data: selectedTrainer } = useUserQuery(
        { user: selectedTrainerIri },
        { enabled: Boolean(selectedTrainerIri) },
    );

    const totalItems = data?.["hydra:totalItems"] ?? 0;

    const options = useMemo(() => {
        const pageOptions =
            data?.["hydra:member"].map(trainer => ({
                value: getResourceIRI(trainer),
                label: trainer.email,
            })) ?? [];

        if (!selectedTrainerIri || !selectedTrainer) {
            return pageOptions;
        }

        return [
            { value: getResourceIRI(selectedTrainer), label: selectedTrainer.email },
            ...pageOptions.filter(option => option.value !== selectedTrainerIri),
        ];
    }, [data, selectedTrainer, selectedTrainerIri]);

    const searchTrainers = (nextSearch: string) => {
        setSearch(nextSearch);
        setCurrentPage(1);
    };

    return (
        <Select
            {...rest}
            showSearch
            filterOption={false}
            searchValue={search}
            onSearch={searchTrainers}
            value={value}
            loading={isLoading}
            notFoundContent={isLoading ? <Skeleton title={false} /> : undefined}
            onChange={onChange ?? onSelect}
            popupMatchSelectWidth={false}
            options={options}
            popupRender={(menu: ReactNode) => (
                <>
                    {menu}
                    {totalItems > RESULTS_PER_PAGE ? (
                        <>
                            <Divider className="my-ant" />
                            <Pagination
                                className="mb-ant"
                                current={currentPage}
                                total={totalItems}
                                pageSize={RESULTS_PER_PAGE}
                                onChange={setCurrentPage}
                                size="small"
                                align="center"
                                hideOnSinglePage
                                showSizeChanger={false}
                                simple
                            />
                        </>
                    ) : null}
                </>
            )}
        />
    );
};
